import type { CSSProperties } from 'react'
import { createTheme, ThemeOptions } from '@mui/material/styles'
import { enUS, Localization } from '@mui/material/locale'

import breakpoints from './breakpoints'
import componentsOverride from './overrides'
import palette from './palette'
import shadows from './shadows'
import typography from './typography'
import GlobalStyles from './globalStyles'

/**
 * Types
 */

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string
    dark: string
  }
  interface SimplePaletteColorOptions {
    lighter: string
    darker: string
  }
  interface PaletteColor {
    lighter: string
    darker: string
  }
  interface TypeText {
    /** Metadata, helper text, timestamps — the lightest ink that still passes 4.5:1. Not the same as disabled. */
    tertiary: string
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    /** App view heading — see typography.ts */
    pageTitle: CSSProperties
    /** Number face for tabular data — see typography.ts */
    data: CSSProperties
    /** Number face at h4 size — see typography.ts */
    dataMedium: CSSProperties
    /** Number face at h2 size, for a card's hero value — see typography.ts */
    dataDisplay: CSSProperties
    /** Data face: counts, dates, IDs, metrics. Use via theme.typography.fontFamilyMono */
    fontFamilyMono: string
  }
  interface TypographyVariantsOptions {
    pageTitle?: CSSProperties
    data?: CSSProperties
    dataMedium?: CSSProperties
    dataDisplay?: CSSProperties
    fontFamilyMono?: string
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true
    data: true
    dataMedium: true
    dataDisplay: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/Pagination' {
  interface PaginationPropsColorOverrides {
    info: true
    success: true
    warning: true
    error: true
  }
}

/**
 * Provider
 */

export function getTheme(locale: Localization = enUS, mode: 'light' | 'dark' = 'light') {
  const isLight = mode === 'light'
  const basePalette = isLight ? palette.light : palette.dark
  const themeOptions: ThemeOptions = {
    palette: basePalette,
    typography,
    breakpoints,
    shape: { borderRadius: 8 },
    direction: 'ltr',
    shadows: isLight ? shadows.light : shadows.dark,
    /**
     * Motion tokens live in MUI's own slots so Menu, Dialog, Collapse and
     * every transition helper pick them up without a parallel system.
     * Three durations (120 micro / 200 local / 300 overlay), entrances on
     * ease-out, exits on ease-in and always faster than entering.
     */
    transitions: {
      easing: {
        easeOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        easeIn: 'cubic-bezier(0.45, 0, 0.7, 0.2)',
        easeInOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        sharp: 'cubic-bezier(0.45, 0, 0.7, 0.2)',
      },
      duration: {
        shortest: 120,
        shorter: 120,
        short: 200,
        standard: 200,
        complex: 300,
        enteringScreen: 200,
        leavingScreen: 120,
      },
    },
  }

  const theme = createTheme(themeOptions, locale)

  theme.components = componentsOverride(theme)
  return theme
}

export { GlobalStyles }
export { OVERLAY_Z_INDEX } from './zIndex'
